import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import InputGroup from '~/components/Inputs/InputGroup'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import {
  keys as authorizationsKeys,
  useCreateAuthorizationMutation,
  useUpdateAuthorizationMutation,
} from '~/queries/iam-authorizations'

const validationSchema = Yup.object().shape({
  action: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  description: Yup.string(),
  resource: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  service: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface FormValues {
  action: string
  description?: string
  resource: string
  service: string
}

interface FormProps {
  action?: string
  authKey?: string
  description?: string
  onClose: VoidFunction
  onFailure: (e: unknown) => void
  onSuccess: VoidFunction
  resource?: string
  service?: string
}

const ManageAuthorizationForm = ({
  action,
  authKey,
  description,
  onClose,
  onFailure,
  onSuccess,
  resource,
  service,
}: FormProps): ReactElement => {
  /** Local state */

  const defaultValues = {
    action: action || '',
    description: description || '',
    resource: resource || '',
    service: service || '',
  }

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const queryClient = useQueryClient()

  /** Queries & Mutations */

  const { mutateAsync: createAuthorization } = useCreateAuthorizationMutation()

  const { mutateAsync: updateAuthorization } = useUpdateAuthorizationMutation()

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      if (authKey) {
        if (values.description !== description) {
          await updateAuthorization({
            authorizationKey: authKey,
            description: values.description,
          })
        }
        await queryClient.invalidateQueries({ queryKey: authorizationsKeys.details(authKey) })
      } else {
        await createAuthorization([{ ...values, description: values.description || '' }])
      }
      await queryClient.invalidateQueries({ queryKey: authorizationsKeys.list() })
      onSuccess()
    } catch (e) {
      onFailure(e)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <InputGroup>
          {authKey ? <TextInput disabled label="Key" name="key" value={authKey} /> : null}
          <TextInput
            {...register('service')}
            disabled={!!service}
            error={errors.service?.message}
            label="Service"
            placeholder="Enter service"
            required
          />
          <TextInput
            {...register('resource')}
            disabled={!!resource}
            error={errors.resource?.message}
            label="Resource"
            placeholder="Enter resource"
            required
          />
          <TextInput
            {...register('action')}
            disabled={!!action}
            error={errors.action?.message}
            label="Action"
            placeholder="Enter action"
            required
          />
          <TextInput
            {...register('description')}
            error={errors.description?.message}
            label="Description"
            placeholder="Enter description"
          />
        </InputGroup>
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const ManageAuthorizationModal = ({ authKey, isOpen, onClose, ...props }: ModalProps): ReactElement => {
  return (
    <Modal className="manage-authorization-modal" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{authKey ? 'Edit' : 'Create'} authorization information</ModalHeader>
      <ManageAuthorizationForm {...props} authKey={authKey} onClose={onClose} />
    </Modal>
  )
}

export default ManageAuthorizationModal
